import React, { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, CheckCircle, XCircle, MinusCircle, ChefHat } from 'lucide-react';

/* ═══════════════════════════════════════════
   MAIN ROUTER — picks the right review renderer
   ═══════════════════════════════════════════ */
export function GameReview({ reviewType, reviewData, gameName, settings }) {
  const [expanded, setExpanded] = useState(true);
  if (!reviewData || reviewData.length === 0) return null;

  const totalCount = reviewType === 'category'
    ? reviewData.reduce((s, set) => s + (set.words?.length || 0), 0)
    : reviewType === 'pairs'
    ? reviewData.reduce((s, set) => s + (set.pairs?.length || 0), 0)
    : reviewData.length;

  return (
    <div data-testid="game-question-review" style={{
      width: '100%', maxWidth: 900,
      background: 'var(--surface)', border: '2px solid var(--border)',
      borderRadius: '1.25rem', overflow: 'hidden',
    }}>
      <button data-testid="review-toggle" onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '1rem 1.25rem', background: 'none', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <BookOpen size={22} color="var(--primary)" />
          <span style={{ fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.3rem', color: 'var(--text-primary)' }}>
            Question Review
          </span>
          <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            {totalCount} {reviewType === 'pairs' ? 'pairs' : reviewType === 'sentence' ? 'sentences' : reviewType === 'passage' ? 'passages' : reviewType === 'recipe' ? 'recipes' : 'items'}
          </span>
        </div>
        {expanded ? <ChevronUp size={22} color="var(--text-muted)" /> : <ChevronDown size={22} color="var(--text-muted)" />}
      </button>

      {expanded && (
        <div style={{ padding: '0 1rem 1.25rem', maxHeight: '55vh', overflowY: 'auto' }}>
          {reviewType === 'team_qa' && <TeamQAReview data={reviewData} settings={settings} />}
          {reviewType === 'qa' && <QAReview data={reviewData} />}
          {reviewType === 'category' && <CategoryReview data={reviewData} />}
          {reviewType === 'pairs' && <PairsReview data={reviewData} />}
          {reviewType === 'sentence' && <SentenceReview data={reviewData} />}
          {reviewType === 'passage' && <PassageReview data={reviewData} />}
          {reviewType === 'recipe' && <RecipeReview data={reviewData} />}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   QA REVIEW — Solo Q&A games
   (L'Horloge, La Tour, Le Detective, Carte du Canada)
   ═══════════════════════════════════════════ */
function QAReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {data.map((q, i) => (
        <QACard key={i} q={q} />
      ))}
    </div>
  );
}

function QACard({ q }) {
  const isCorrect = q.wasCorrect === true;
  const isWrong = q.wasCorrect === false;
  const notAttempted = q.wasCorrect === null;

  return (
    <div style={{
      background: isCorrect ? 'rgba(47,158,68,0.06)' : isWrong ? 'rgba(224,49,49,0.06)' : 'rgba(0,0,0,0.02)',
      borderRadius: '1rem', padding: '0.85rem 1rem',
      border: `2px solid ${isCorrect ? 'rgba(47,158,68,0.15)' : isWrong ? 'rgba(224,49,49,0.15)' : 'rgba(0,0,0,0.05)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.3rem' }}>
        <div style={{ flexShrink: 0, marginTop: 2 }}>
          {isCorrect && <CheckCircle size={22} color="var(--success)" />}
          {isWrong && <XCircle size={22} color="var(--error)" />}
          {notAttempted && <MinusCircle size={22} color="#aaa" />}
        </div>
        <span style={{ fontFamily: 'Fredoka', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {q.prompt}
        </span>
      </div>

      {/* Correct answer — always shown big and green */}
      <div style={{
        fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 800,
        color: 'var(--success)', marginLeft: '2rem',
      }}>
        {q.correctAnswer}
      </div>

      {/* Wrong answer if applicable */}
      {isWrong && q.userAnswer && (
        <div style={{
          fontSize: '1rem', color: 'var(--error)', fontWeight: 600,
          marginLeft: '2rem', marginTop: '0.15rem', textDecoration: 'line-through', opacity: 0.7,
        }}>
          {q.userAnswer}
        </div>
      )}

      {notAttempted && (
        <div style={{ fontSize: '0.85rem', color: '#999', fontWeight: 600, marginLeft: '2rem', marginTop: '0.1rem' }}>
          Not reached
        </div>
      )}

      {/* Exercise type badge if provided */}
      {q.type && (
        <span style={{
          display: 'inline-block', marginLeft: '2rem', marginTop: '0.3rem',
          padding: '0.1rem 0.5rem', borderRadius: '0.3rem',
          background: 'rgba(76,110,245,0.08)', fontSize: '0.75rem',
          fontWeight: 700, color: '#4C6EF5', fontFamily: 'Fredoka',
        }}>
          {q.type}
        </span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   TEAM QA REVIEW — Team Q&A games (La Course)
   ═══════════════════════════════════════════ */
function TeamQAReview({ data, settings }) {
  const t1 = data.filter(q => q.team === 1);
  const t2 = data.filter(q => q.team === 2);
  const maxLen = Math.max(t1.length, t2.length);

  return (
    <>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem',
        position: 'sticky', top: 0, zIndex: 2, paddingBottom: '0.25rem',
      }}>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 800,
          color: 'white', textAlign: 'center',
          padding: '0.5rem', background: 'var(--team1)', borderRadius: '0.75rem',
        }}>{settings?.team1Name || 'Team 1'}</div>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 800,
          color: 'white', textAlign: 'center',
          padding: '0.5rem', background: 'var(--team2)', borderRadius: '0.75rem',
        }}>{settings?.team2Name || 'Team 2'}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {Array.from({ length: maxLen }).map((_, i) => (
          <React.Fragment key={i}>
            <QACard q={t1[i] || { prompt: '', correctAnswer: '', wasCorrect: null }} />
            <QACard q={t2[i] || { prompt: '', correctAnswer: '', wasCorrect: null }} />
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════
   CATEGORY REVIEW — L'Avalanche, Tri Express
   ═══════════════════════════════════════════ */
function CategoryReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {data.map((set, si) => (
        <div key={si}>
          {/* Set header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem',
            paddingBottom: '0.4rem', borderBottom: '2px solid rgba(0,0,0,0.06)',
          }}>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 700,
              color: 'var(--text-muted)', textTransform: 'uppercase',
            }}>
              {set.setLabel || `Set ${si + 1}`}
            </span>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
              color: 'var(--primary)',
            }}>
              {set.setTitle}
            </span>
          </div>

          {/* Category labels */}
          {set.categories && set.categories.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {set.categories.map((cat, ci) => (
                <span key={ci} style={{
                  padding: '0.15rem 0.6rem', borderRadius: '2rem',
                  background: `${cat.color || '#4C6EF5'}18`, fontFamily: 'Fredoka',
                  fontSize: '0.85rem', fontWeight: 700, color: cat.color || '#4C6EF5',
                }}>
                  {cat.label}
                </span>
              ))}
            </div>
          )}

          {/* Words */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {(set.words || []).map((w, wi) => (
              <span key={wi} style={{
                padding: '0.35rem 0.85rem', borderRadius: '0.75rem',
                fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
                background: w.wasCorrect ? 'rgba(47,158,68,0.1)' : w.wasCorrect === false ? 'rgba(224,49,49,0.08)' : 'rgba(0,0,0,0.03)',
                border: `2px solid ${w.wasCorrect ? 'rgba(47,158,68,0.2)' : w.wasCorrect === false ? 'rgba(224,49,49,0.2)' : 'rgba(0,0,0,0.06)'}`,
                color: w.wasCorrect ? 'var(--success)' : w.wasCorrect === false ? 'var(--error)' : 'var(--text-secondary)',
              }}>
                {w.text}
                {w.correctCategory && (
                  <span style={{
                    marginLeft: '0.3rem', fontSize: '0.7rem', opacity: 0.7,
                    fontWeight: 600,
                  }}>
                    ({w.correctCategory})
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PAIRS REVIEW — La Memoire
   ═══════════════════════════════════════════ */
function PairsReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {data.map((set, si) => (
        <div key={si}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem',
            paddingBottom: '0.4rem', borderBottom: '2px solid rgba(0,0,0,0.06)',
          }}>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 700,
              color: 'var(--text-muted)', textTransform: 'uppercase',
            }}>Set {si + 1}</span>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700, color: 'var(--primary)',
            }}>{set.setTitle}</span>
            {set.matchType && (
              <span style={{
                padding: '0.1rem 0.5rem', borderRadius: '0.3rem',
                background: 'rgba(76,110,245,0.08)', fontSize: '0.75rem',
                fontWeight: 700, color: '#4C6EF5', fontFamily: 'Fredoka',
              }}>{set.matchType}</span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
            {(set.pairs || []).map((p, pi) => (
              <div key={pi} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: 'rgba(47,158,68,0.05)', borderRadius: '0.75rem',
                padding: '0.6rem 0.85rem', border: '2px solid rgba(47,158,68,0.12)',
              }}>
                <span style={{
                  fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
                  color: '#4C6EF5', flex: 1,
                }}>{p.a}</span>
                <span style={{ color: '#999', fontWeight: 600, fontSize: '1rem' }}>=</span>
                <span style={{
                  fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
                  color: 'var(--success)', flex: 1, textAlign: 'right',
                }}>{p.b}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SENTENCE REVIEW — Batisseur
   ═══════════════════════════════════════════ */
function SentenceReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {data.map((s, i) => (
        <div key={i} style={{
          background: s.wasCorrect ? 'rgba(47,158,68,0.06)' : 'rgba(245,159,0,0.06)',
          borderRadius: '1rem', padding: '0.85rem 1rem',
          border: `2px solid ${s.wasCorrect ? 'rgba(47,158,68,0.15)' : 'rgba(245,159,0,0.15)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            {s.wasCorrect ? <CheckCircle size={20} color="var(--success)" /> : <XCircle size={20} color="#F59F00" />}
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {s.english}
            </span>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginLeft: '1.85rem',
          }}>
            {(s.correctWords || []).map((w, wi) => (
              <span key={wi} style={{
                padding: '0.25rem 0.7rem', borderRadius: '0.5rem',
                background: 'var(--primary)', color: 'white',
                fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700,
              }}>{w}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PASSAGE REVIEW — Robot Balayeur
   ═══════════════════════════════════════════ */
function PassageReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {data.map((p, i) => (
        <div key={i} style={{
          background: 'rgba(76,110,245,0.04)', borderRadius: '1rem',
          padding: '0.85rem 1rem', border: '2px solid rgba(76,110,245,0.1)',
        }}>
          <div style={{
            fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
            color: 'var(--primary)', marginBottom: '0.4rem',
          }}>
            {p.title}
          </div>
          {p.contentType && (
            <span style={{
              display: 'inline-block', marginBottom: '0.4rem',
              padding: '0.1rem 0.5rem', borderRadius: '0.3rem',
              background: 'rgba(76,110,245,0.08)', fontSize: '0.75rem',
              fontWeight: 700, color: '#4C6EF5', fontFamily: 'Fredoka',
            }}>{p.contentType}</span>
          )}
          <div style={{
            fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 600,
            color: 'var(--text-primary)', lineHeight: 1.6,
          }}>
            {(p.words || []).join(' ')}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ═══════════════════════════════════════════
   RECIPE REVIEW — Le Chef
   Shows each recipe with all steps + correct/wrong
   ═══════════════════════════════════════════ */
function RecipeReview({ data }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {data.map((recipe, ri) => (
        <div key={ri} style={{
          background: recipe.wasComplete ? 'rgba(47,158,68,0.04)' : 'rgba(0,0,0,0.02)',
          borderRadius: '1rem', padding: '1rem',
          border: `2px solid ${recipe.wasComplete ? 'rgba(47,158,68,0.12)' : 'rgba(0,0,0,0.05)'}`,
        }}>
          {/* Recipe header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
            <ChefHat size={22} color="#E8590C" />
            <span style={{ fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.2rem', color: '#E8590C' }}>
              {recipe.recipeName}
            </span>
            {recipe.recipeNameEn && (
              <span style={{ fontFamily: 'Nunito', fontSize: '0.9rem', color: '#888', fontStyle: 'italic' }}>
                ({recipe.recipeNameEn})
              </span>
            )}
            {recipe.wasComplete && <CheckCircle size={18} color="#2F9E44" />}
            {!recipe.wasComplete && <MinusCircle size={18} color="var(--text-muted)" />}
          </div>

          {recipe.culturalOrigin && (
            <div style={{
              fontFamily: 'Nunito', fontSize: '0.85rem', color: '#888', fontStyle: 'italic',
              marginBottom: '0.5rem', paddingLeft: '0.25rem',
            }}>
              {recipe.culturalOrigin}
            </div>
          )}

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {recipe.steps.map((step, si) => {
              const isCorrect = step.wasCorrect === true;
              const isWrong = step.wasCorrect === false;
              const notAttempted = step.wasCorrect === null;

              return (
                <div key={si} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.5rem 0.6rem', borderRadius: '0.6rem',
                  background: isCorrect ? 'rgba(47,158,68,0.06)' : isWrong ? 'rgba(224,49,49,0.06)' : 'rgba(0,0,0,0.02)',
                }}>
                  <div style={{ flexShrink: 0, marginTop: 2 }}>
                    {isCorrect && <CheckCircle size={18} color="#2F9E44" />}
                    {isWrong && <XCircle size={18} color="#E03131" />}
                    {notAttempted && <MinusCircle size={18} color="var(--text-muted)" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                      {step.instruction_fr}
                    </div>
                    {step.instruction_en && (
                      <div style={{ fontFamily: 'Nunito', fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                        {step.instruction_en}
                      </div>
                    )}
                    <div style={{ marginTop: '0.2rem', fontFamily: 'Fredoka', fontSize: '0.9rem' }}>
                      {isCorrect && (
                        <span style={{ color: '#2F9E44' }}>{step.correct_action}</span>
                      )}
                      {isWrong && (
                        <>
                          <span style={{ color: '#E03131', textDecoration: 'line-through', marginRight: '0.5rem' }}>{step.chosen}</span>
                          <span style={{ color: '#2F9E44' }}>{step.correct_action}</span>
                        </>
                      )}
                      {notAttempted && (
                        <span style={{ color: 'var(--text-muted)' }}>Not reached — {step.correct_action}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
